import React, { useState, useEffect, useCallback } from "react";
import { useMutation, useAction, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Doc } from "../../../convex/_generated/dataModel";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Key, CheckCircle2, XCircle, Loader2, ExternalLink, MessageSquare } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface ApifyStatusData {
  connected: boolean;
  user?: { id: string; username: string };
}

interface TelegramBotInfo {
  id: number;
  username: string;
  first_name: string;
}

type Channel = Doc<"channels">;

function ApifyTokenSection() {
  const [token, setToken] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const { toast } = useToast();

  const getStatus = useAction(api.apify.getStatus);
  const validateToken = useAction(api.apify.validateToken);
  const setSetting = useMutation(api.settings.set);

  const [statusData, setStatusData] = useState<ApifyStatusData | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);

  const refreshStatus = useCallback(() => {
    getStatus().then(setStatusData).finally(() => setStatusLoading(false));
  }, [getStatus]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const handleValidateAndSave = async () => {
    if (!token.trim()) return;

    setIsValidating(true);
    try {
      const result = await validateToken({ token });
      if (result.valid) {
        await setSetting({ key: "apify_token", value: token });
        toast({ title: "Apify token saved", description: `Connected as ${result.user?.username}` });
        refreshStatus();
        setToken("");
      } else {
        toast({ title: "Invalid token", description: result.error, variant: "destructive" });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast({ title: "Validation failed", description: message, variant: "destructive" });
    } finally {
      setIsValidating(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await setSetting({ key: "apify_token", value: null });
      refreshStatus();
      toast({ title: "Apify disconnected" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast({ title: "Failed to disconnect", description: message, variant: "destructive" });
    }
  };

  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden">
      <div className="p-4 border-b border-border bg-secondary/10 flex items-center gap-2">
        <Key className="w-4 h-4 text-muted-foreground" />
        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Apify Integration</h3>
      </div>
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm">Status</span>
          {statusLoading ? (
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          ) : (
            <span className={cn(
              "flex items-center gap-1.5 text-xs font-mono px-2 py-0.5 rounded",
              statusData?.connected 
                ? "bg-green-500/10 text-green-600" 
                : "bg-red-500/10 text-red-600"
            )}>
              {statusData?.connected ? (
                <>
                  <CheckCircle2 className="w-3 h-3" />
                  Connected
                </>
              ) : (
                <>
                  <XCircle className="w-3 h-3" />
                  Not connected
                </>
              )}
            </span>
          )}
        </div>

        {statusData?.connected && statusData?.user && (
          <div className="text-xs text-muted-foreground">
            Logged in as <span className="font-medium text-foreground">{statusData.user.username}</span>
          </div>
        )}

        {!statusData?.connected && (
          <>
            <div className="space-y-2">
              <Label className="text-xs">API Token</Label>
              <Input
                type="password"
                placeholder="apify_api_xxxxx..."
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className="font-mono text-sm h-8"
                data-testid="input-apify-token-settings"
              />
            </div>
            <Button 
              onClick={handleValidateAndSave}
              disabled={!token.trim() || isValidating}
              size="sm"
              className="w-full"
              data-testid="button-connect-apify"
            >
              {isValidating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect
            </Button>
          </>
        )}

        {statusData?.connected && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleDisconnect}
            className="w-full"
            data-testid="button-disconnect-apify"
          >
            Disconnect
          </Button>
        )}

        <a
          href="https://console.apify.com/account/integrations"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          Get API token <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}

function TelegramBotSection() {
  const [token, setToken] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  const setSetting = useMutation(api.settings.set);
  const telegramSetting = useQuery(api.settings.get, { key: "telegram_bot_token" }) as { token?: string; botInfo?: TelegramBotInfo } | null | undefined;
  const channels = useQuery(api.channels.list) as Channel[] | undefined;
  const createChannel = useMutation(api.channels.create);
  const updateChannel = useMutation(api.channels.update);

  const [isConnected, setIsConnected] = useState(false);
  const [botInfo, setBotInfo] = useState<TelegramBotInfo | null>(null);

  useEffect(() => {
    if (telegramSetting && typeof telegramSetting === 'object' && 'token' in telegramSetting && telegramSetting.token) {
      setIsConnected(true);
      setBotInfo(telegramSetting.botInfo ?? null);
    } else if (telegramSetting !== undefined) {
      setIsConnected(false);
      setBotInfo(null);
    }
  }, [telegramSetting]);

  const validateAndSave = async () => {
    if (!token.trim()) return;

    setIsSaving(true);
    try {
      // Validate token format (should be like: 123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11)
      const tokenPattern = /^\d+:[A-Za-z0-9_-]+$/;
      if (!tokenPattern.test(token.trim())) {
        toast({
          title: "Invalid token format",
          description: "Telegram bot token should be in format: 123456789:ABC-DEF1234...",
          variant: "destructive"
        });
        setIsSaving(false);
        return;
      }

      // Call Telegram API to validate token
      const response = await fetch(`https://api.telegram.org/bot${token.trim()}/getMe`);
      const data = await response.json();

      if (data.ok) {
        await setSetting({
          key: "telegram_bot_token",
          value: {
            token: token.trim(),
            botInfo: data.result
          }
        });

        // Create or update the Telegram channel
        const existingChannel = channels?.find(c => c.type === "Telegram");
        if (existingChannel) {
          await updateChannel({
            id: existingChannel._id,
            name: `Telegram Bot (@${data.result.username})`,
            target: `@${data.result.username}`,
            status: "Active",
            config: { botId: data.result.id }
          });
        } else {
          await createChannel({
            type: "Telegram",
            name: `Telegram Bot (@${data.result.username})`,
            target: `@${data.result.username}`,
            config: { botId: data.result.id }
          });
        }

        setIsConnected(true);
        setBotInfo(data.result);
        setToken("");
        toast({
          title: "Telegram bot connected",
          description: `Connected as @${data.result.username}`
        });
      } else {
        toast({
          title: "Invalid token",
          description: data.description || "Token validation failed",
          variant: "destructive"
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast({
        title: "Connection failed",
        description: message,
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await setSetting({
        key: "telegram_bot_token",
        value: { token: null, botInfo: null }
      });
      setIsConnected(false);
      setBotInfo(null);
      toast({ title: "Telegram bot disconnected" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast({
        title: "Failed to disconnect",
        description: message,
        variant: "destructive"
      });
    }
  };

  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden">
      <div className="p-4 border-b border-border bg-secondary/10 flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-muted-foreground" />
        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Telegram Bot</h3>
      </div>
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm">Status</span>
          <span className={cn(
            "flex items-center gap-1.5 text-xs font-mono px-2 py-0.5 rounded",
            isConnected
              ? "bg-green-500/10 text-green-600"
              : "bg-red-500/10 text-red-600"
          )}>
            {isConnected ? (
              <>
                <CheckCircle2 className="w-3 h-3" />
                Connected
              </>
            ) : (
              <>
                <XCircle className="w-3 h-3" />
                Not connected
              </>
            )}
          </span>
        </div>

        {isConnected && botInfo && (
          <div className="space-y-2 text-xs">
            <div className="text-muted-foreground">
              Bot: <span className="font-medium text-foreground">@{botInfo.username}</span>
            </div>
            <div className="text-muted-foreground">
              Name: <span className="font-medium text-foreground">{botInfo.first_name}</span>
            </div>
            {botInfo.id && (
              <div className="text-muted-foreground">
                Bot ID: <span className="font-mono text-foreground">{botInfo.id}</span>
              </div>
            )}
          </div>
        )}

        {!isConnected && (
          <>
            <div className="space-y-2">
              <Label className="text-xs">Bot Token</Label>
              <Input
                type="password"
                placeholder="8426756108:AAF2_gySpQENZPnq..."
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className="font-mono text-sm h-8"
                data-testid="input-telegram-token-settings"
              />
            </div>
            <Button
              onClick={validateAndSave}
              disabled={!token.trim() || isSaving}
              size="sm"
              className="w-full"
              data-testid="button-connect-telegram"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect
            </Button>
          </>
        )}

        {isConnected && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleDisconnect}
            className="w-full"
            data-testid="button-disconnect-telegram"
          >
            Disconnect
          </Button>
        )}

        <a
          href="https://t.me/BotFather"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          Create bot with @BotFather <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}

export default function Settings() {
  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Configure integrations and system preferences.</p>
      </div>

      <div className="space-y-6">
        <TelegramBotSection />
        <ApifyTokenSection />
      </div>
    </div>
  );
}
